<template>
  <div class="channel-match-history-page">
    <!-- 头部 -->
    <div class="match-history-header">
      <van-icon name="arrow-left" size="20" color="#333" style="cursor: pointer;" @click="$router.back()" />
      <span class="match-history-title">{{ $t('channel_matchHistory_title') }}</span>
      <span></span>
    </div>

    <!-- 加载中 -->
    <div v-if="loading && matches.length === 0" style="display: flex; justify-content: center; padding: 40px;">
      <van-loading size="24" />
    </div>

    <!-- 空状态 -->
    <van-empty v-else-if="matches.length === 0" :description="$t('chatSessionMatch_noMatches')" />

    <!-- 新匹配结果通知提示 -->
    <div v-else class="match-notification-hint">
      <van-icon name="info-o" size="14" color="#E8543A" style="flex-shrink: 0; margin-top: 1px;" />
      <span class="match-notification-hint-text">{{ $t('match_new_result_hint') }}</span>
    </div>

    <!-- 匹配列表 -->
    <div class="match-list" ref="matchList" @scroll="onScroll">
      <div v-for="(match, index) in matches" :key="index" class="match-card" @click="goToMatchedSession(match)">
        <!-- 匹配标签：绝对右上角 -->
        <div class="match-badge" :class="match.isMatched == 1 ? 'badge-matched' : 'badge-interested'">
          {{ match.isMatched == 1 ? $t('label_perfect_match') : $t('label_worth_a_look') }}
        </div>

        <!-- 上方用户 -->
        <div class="match-user-row">
          <img v-if="match.avatar1" :src="$common.getFullImageUrl(match.avatar1, $config, 's')" class="match-avatar" />
          <div v-else class="match-avatar-placeholder">{{ (match.userName1 || '?')[0] }}</div>
          <div class="match-user-info">
            <div class="match-user-name">{{ match.userName1 || $t('anonymous') }}</div>
            <div v-if="match.userMsg1 || match.aiSummary1" class="match-user-content">
              {{ $common.formatUserMsgPreview(match.userMsg1) || match.aiSummary1 }}
            </div>
            <div v-if="match.aiSummary1 && match.userMsg1 && match.aiSummary1 != match.userMsg1" class="match-user-ai-summary">
              <span class="ai-summary-label">{{ $t('aiChat_matchSummaryLabel') }}</span> {{ match.aiSummary1 }}
            </div>
          </div>
        </div>

        <!-- 连接符号 -->
        <div class="match-link-row">
          <div class="match-link-line"></div>
          <van-icon name="link-o" size="12" color="#999" />
          <div class="match-link-line"></div>
        </div>

        <!-- 下方用户 -->
        <div class="match-user-row">
          <img v-if="match.avatar2" :src="$common.getFullImageUrl(match.avatar2, $config, 's')" class="match-avatar" />
          <div v-else class="match-avatar-placeholder">{{ (match.userName2 || '?')[0] }}</div>
          <div class="match-user-info">
            <div class="match-user-name">{{ match.userName2 || $t('anonymous') }}</div>
            <div v-if="match.userMsg2 || match.aiSummary2" class="match-user-content">
              {{ $common.formatUserMsgPreview(match.userMsg2) || match.aiSummary2 }}
            </div>
            <div v-if="match.aiSummary2 && match.userMsg2 && match.aiSummary2 != match.userMsg2" class="match-user-ai-summary">
              <span class="ai-summary-label">{{ $t('aiChat_matchSummaryLabel') }}</span> {{ match.aiSummary2 }}
            </div>
          </div>
        </div>

        <!-- 时间 -->
        <div v-if="match.matchedAt > 0" class="match-time">{{ $common.formatMMDDHHMM(match.matchedAt) }}</div>
      </div>

      <!-- 加载更多 -->
      <div v-if="loading && hasMore" style="text-align: center; padding: 16px;">
        <van-loading size="20" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Loading, Empty } from 'vant'

Vue.use(Icon)
Vue.use(Loading)
Vue.use(Empty)

export default {
  name: 'ChannelMatchHistory',
  data() {
    return {
      channelId: '',
      matches: [],
      loading: false,
      hasMore: true,
      curPage: 1,
      pageSize: 20,
      total: 0
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.channelId = String(newVal)
          this.loadMatches()
        }
      }
    }
  },
  methods: {
    async loadMatches() {
      if (this.loading) return
      this.loading = true
      this.curPage = 1
      try {
        const response = await axios.post('/api/channel/match_history', {
          channelId: this.channelId,
          pageSize: this.pageSize,
          curPage: 1
        })
        if (response.data.code === 0 && response.data.data) {
          this.matches = response.data.data.matches || []
          this.total = response.data.data.total || 0
          this.hasMore = this.matches.length < this.total
        } else {
          this.matches = []
          this.hasMore = false
        }
      } catch (error) {
        console.error('[ChannelMatchHistory] loadMatches error:', error)
        this.matches = []
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (this.loading || !this.hasMore) return
      this.loading = true
      this.curPage++
      try {
        const response = await axios.post('/api/channel/match_history', {
          channelId: this.channelId,
          pageSize: this.pageSize,
          curPage: this.curPage
        })
        if (response.data.code === 0 && response.data.data) {
          const newMatches = response.data.data.matches || []
          this.matches = this.matches.concat(newMatches)
          this.total = response.data.data.total || 0
          this.hasMore = this.matches.length < this.total
        } else {
          this.hasMore = false
        }
      } catch (error) {
        console.error('[ChannelMatchHistory] loadMore error:', error)
        this.hasMore = false
      } finally {
        this.loading = false
      }
    },
    onScroll() {
      const el = this.$refs.matchList
      if (!el || this.loading || !this.hasMore) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.loadMore()
      }
    },
    goToMatchedSession(match) {
      const url = `/app/#/matched-session?currentChatSessionId=${match.chatSessionId1}&matchedChatSessionId=${match.chatSessionId2}`
      window.open(url, '_blank')
    },

  }
}
</script>

<style scoped>
.channel-match-history-page {
  background: #F7F5F2;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.match-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.match-history-title {
  font-size: 16px;
  font-weight: 500;
}

.match-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 24px;
}

.match-card {
  position: relative;
  margin: 6px 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #E0DDD8;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  cursor: pointer;
}

.match-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
}

.match-user-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.match-avatar {
  width: 40px;
  height: 40px;
  border-radius: 16%;
  object-fit: cover;
  flex-shrink: 0;
}

.match-avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 16%;
  background: #E8543A;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.match-user-info {
  flex: 1;
  min-width: 0;
}

.match-user-name {
  font-size: 13px;
  font-weight: 500;
  color: #1A1A1A;
  text-align: left;
}

.match-user-content {
  font-size: 11px;
  color: #8A8A8A;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-align: left;
}

.match-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  flex-shrink: 0;
}

.badge-matched {
  background: #FDEAEA;
  color: #E8543A;
  border: 1px solid #FDEAEA;
}

.badge-interested {
  background: #F7F5F2;
  color: #8A8A8A;
  border: 1px solid #ddd;
}

.match-link-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  padding-left: 20px;
}

.match-link-line {
  flex: 1;
  height: 1px;
  background: #eee;
}

.match-user-ai-summary {
  font-size: 10px;
  color: #8A8A8A;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-align: left;
}

.ai-summary-label {
  font-size: 10px;
  color: #E8543A;
  background: #FEF0ED;
  padding: 1px 6px;
  border-radius: 4px;
  margin-right: 4px;
}

.match-time {
  font-size: 10px;
  color: #bbb;
  margin-top: 8px;
}

.match-notification-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 12px;
  padding: 10px 12px;
  background: #FEF9E7;
  border-radius: 8px;
  border: 1px solid #FEF9E7;
}

.match-notification-hint-text {
  font-size: 12px;
  color: #8A8A8A;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// src/components/ChannelMatchHistory.vue